// Tiny `fetch` wrapper that:
//   - prefixes nothing (we use vite proxy in dev + same-origin in prod)
//   - adds the in-memory access JWT to Authorization
//   - on 401, renews the session (lib/session-refresh.ts: single flight,
//     retried through deploys, signs out only on a definitive 401) and retries
//   - throws an ApiError with the response body shape so callers can switch
//     on err.status / err.body
//
// Auth state lives in AuthProvider — this module reads it via the
// getter/setter functions registered at AuthProvider mount time. That
// keeps the wrapper free of React imports while still letting the
// provider control + react to token state.
import type { RefreshOutcome } from "./session-refresh";

let getAccessToken: (() => string | null) = () => null;
let refreshToken: () => Promise<RefreshOutcome> = async () => ({ kind: "signed_out" });
let onLogout: () => void = () => {};

export function bindAuth(args: {
  getAccessToken: () => string | null;
  refreshToken: () => Promise<RefreshOutcome>;
  onLogout: () => void;
}): void {
  getAccessToken = args.getAccessToken;
  refreshToken = args.refreshToken;
  onLogout = args.onLogout;
}

export class ApiError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, body: unknown, message?: string) {
    super(message ?? `HTTP ${status}`);
    this.status = status;
    this.body = body;
  }
}

/** The server's own error text when it sent one, else the fallback. */
export function apiErrorMessage(e: unknown, fallback: string): string {
  if (e instanceof ApiError) {
    const body = e.body as { error?: unknown } | null;
    if (body && typeof body.error === "string") return body.error;
    return e.message;
  }
  return e instanceof Error ? e.message : fallback;
}

export interface ApiOptions {
  /** Skip auto-refresh on 401 — used for the login + refresh calls themselves. */
  skipAuthRefresh?: boolean;
  /** Skip Authorization header — used for unauthenticated public routes. */
  skipAuth?: boolean;
}

async function rawFetch(
  input: RequestInfo,
  init: RequestInit,
): Promise<Response> {
  return fetch(input, {
    credentials: "include", // sends merchd_refresh cookie on /auth/refresh
    ...init,
  });
}

/**
 * The access token ran out (401): renew it. Only a definitive "signed out"
 * from the server signs the person out; a deploy or a blip that outlasts the
 * retries throws a 503 and KEEPS the session (stay signed in, 2026-10-22 —
 * lib/session-refresh.ts).
 */
async function renewedToken(): Promise<string> {
  const outcome = await refreshToken();
  if (outcome.kind === "ok") return outcome.accessToken;
  if (outcome.kind === "signed_out") {
    onLogout();
    throw new ApiError(401, { error: "session_expired" }, "session expired");
  }
  throw new ApiError(
    503,
    { error: outcome.message ?? "Can't reach the server right now — you're still signed in. Try again in a moment." },
    "server unavailable",
  );
}

export async function api<T = unknown>(
  path: string,
  init: RequestInit & ApiOptions = {},
): Promise<T> {
  const { skipAuthRefresh, skipAuth, ...rest } = init;

  const headers = new Headers(rest.headers);
  if (rest.body !== undefined && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  // Always send Accept: application/json so the Express layer can
  // disambiguate fetch-from-SPA (wants JSON) from browser-navigation
  // (wants HTML — gets the SPA shell). Without this, a fetch('/clients')
  // could be served the HTML shell instead of the API payload.
  if (!headers.has("Accept")) {
    headers.set("Accept", "application/json");
  }
  if (!skipAuth) {
    const tok = getAccessToken();
    if (tok) headers.set("Authorization", `Bearer ${tok}`);
  }

  let res = await rawFetch(path, { ...rest, headers });

  if (res.status === 401 && !skipAuthRefresh && !skipAuth) {
    const newTok = await renewedToken();
    const retryHeaders = new Headers(headers);
    retryHeaders.set("Authorization", `Bearer ${newTok}`);
    res = await rawFetch(path, { ...rest, headers: retryHeaders });
  }

  if (!res.ok) {
    let body: unknown = null;
    try {
      body = await res.json();
    } catch {
      // ignore — body may be empty/text
    }
    throw new ApiError(res.status, body);
  }

  // 204 No Content
  if (res.status === 204) return undefined as T;
  // Some routes return text; default to JSON
  const ct = res.headers.get("Content-Type") ?? "";
  if (ct.includes("application/json")) {
    return (await res.json()) as T;
  }
  return (await res.text()) as unknown as T;
}

/** Authenticated GET that returns the raw bytes (PDF / PNG downloads). */
export async function apiBlob(path: string): Promise<Blob> {
  const headers = new Headers({ Accept: "*/*" });
  const tok = getAccessToken();
  if (tok) headers.set("Authorization", `Bearer ${tok}`);
  let res = await rawFetch(path, { headers });
  if (res.status === 401) {
    const newTok = await renewedToken();
    headers.set("Authorization", `Bearer ${newTok}`);
    res = await rawFetch(path, { headers });
  }
  if (!res.ok) {
    let body: unknown = null;
    try {
      body = await res.json();
    } catch {
      // not JSON
    }
    throw new ApiError(res.status, body);
  }
  return res.blob();
}
